import player from "../../../../player";

if (process.env.WEBPACK) {
    (window => {
        if (window.AnalyserNode && !window.AnalyserNode.prototype.getFloatTimeDomainData) {
            const uint8 = new Uint8Array(2048);
            window.AnalyserNode.prototype.getFloatTimeDomainData = function (array) {
                this.getByteTimeDomainData(uint8);
                for (let i = 0, imax = array.length; i < imax; i++) {
                    array[i] = (uint8[i] - 128) * 0.0078125;
                }
            };
        }
    })(window);
}

class Recorder {
    analyser: AnalyserNode | null;
    handleMediaError: () => void;
    gotRecInput: (_pitch: number) => void;
    mediaStreamSource: MediaStreamAudioSourceNode | null
    recentNotes: number[] = [];
    globalStream: MediaStream | null = null;
    rafID?: ReturnType<typeof window.requestAnimationFrame>
    constructor(gotRecInput: (_pitch: number) => void, handleMediaError: () => void) {
        this.analyser = null;
        this.handleMediaError = handleMediaError;
        this.mediaStreamSource = null;
        this.gotRecInput = gotRecInput;
    }

    getUserMedia = (dictionary: { audio: boolean, video: boolean }, callback: (stream: MediaStream) => void) => {
        try {
            navigator.mediaDevices.getUserMedia =
                navigator.mediaDevices.getUserMedia ||
                // @ts-ignore
                navigator.mediaDevices.webkitGetUserMedia ||
                // @ts-ignore
                navigator.mediaDevices.mozGetUserMedia;
            navigator.mediaDevices.getUserMedia(dictionary).then(callback).catch(this.handleMediaError);
        } catch (e) {
            alert("getUserMedia threw exception :" + e); //TODO
        }
    };

    gotStream = (stream: MediaStream) => {
        // Create an AudioNode from the stream.
        this.mediaStreamSource = player.context.createMediaStreamSource(stream);
        this.globalStream = stream;

        // Connect it to the destination.
        this.analyser = player.context.createAnalyser();
        this.analyser.fftSize = 2048;
        this.mediaStreamSource.connect(this.analyser);
        this.updatePitch();
    };

    toggleLiveInput = (recording: boolean) => {
        if (!recording) {
            if (this.globalStream) {
                const track = this.globalStream.getTracks()[0]; // if only one media track
                track.stop();
            }
            if (!window.cancelAnimationFrame) window.cancelAnimationFrame = (window as any).webkitCancelAnimationFrame;
            window.cancelAnimationFrame(this.rafID!);
            this.mediaStreamSource = null;
        } else {
            this.getUserMedia(
                {
                    audio: true,
                    video: false,
                },
                this.gotStream
            );
        }
    };

    buflen = 1024;
    buf = new Float32Array(this.buflen);

    noteFromPitch = (frequency: number) => {
        const noteNum = 12 * (Math.log(frequency / 440) / Math.log(2));
        return Math.round(noteNum) + 69;
    };

    MIN_SAMPLES = 0; // will be initialized when AudioContext is created.
    GOOD_ENOUGH_CORRELATION = 0.9; // this is the "bar" for how close a correlation needs to be

    autoCorrelate = (buf: Float32Array, sampleRate: number) => {
        const SIZE = buf.length;
        const MAX_SAMPLES = Math.floor(SIZE / 2);
        let best_offset = -1;
        let best_correlation = 0;
        let rms = 0;
        let foundGoodCorrelation = false;
        const correlations = new Array(MAX_SAMPLES);

        for (let i = 0; i < SIZE; i++) {
            const val = buf[i];
            rms += val * val;
        }
        rms = Math.sqrt(rms / SIZE);
        if (rms < 0.01)
            // not enough signal
            return -1;

        let lastCorrelation = 1;
        for (let offset = this.MIN_SAMPLES; offset < MAX_SAMPLES; offset++) {
            let correlation = 0;

            for (let i = 0; i < MAX_SAMPLES; i++) {
                correlation += Math.abs(buf[i] - buf[i + offset]);
            }
            correlation = 1 - correlation / MAX_SAMPLES;
            correlations[offset] = correlation; // store it, for the tweaking we need to do below.
            if (correlation > this.GOOD_ENOUGH_CORRELATION && correlation > lastCorrelation) {
                foundGoodCorrelation = true;
                if (correlation > best_correlation) {
                    best_correlation = correlation;
                    best_offset = offset;
                }
            } else if (foundGoodCorrelation) {
                const shift =
                    (correlations[best_offset + 1] - correlations[best_offset - 1]) / correlations[best_offset];
                return sampleRate / (best_offset + 8 * shift);
            }
            lastCorrelation = correlation;
        }
        if (best_correlation > 0.01) {
            return sampleRate / best_offset;
        }
        return -1;
    };

    updatePitch = () => {
        let tooSoft = false;

        const array = new Uint8Array(this.analyser!.frequencyBinCount);
        this.analyser!.getByteFrequencyData(array);
        const average = this.getAverageVolume(array);
        if (average < 8) tooSoft = true;

        if (!tooSoft) {
            this.analyser!.getFloatTimeDomainData(this.buf);
            const ac = this.autoCorrelate(this.buf, player.context.sampleRate);
            if (ac === -1) {
                this.recentNotes.length = 0;
            } else {
                const pitch = ac;
                const note = this.noteFromPitch(pitch);
                this.recentNotes.push(note);
                //shorten it
                if (this.recentNotes.length > 10) {
                    this.recentNotes.shift();
                }
                //get the most regular one from the array
                const notesCount = [];
                for (let a = 0; a < this.recentNotes.length; a++) {
                    if (notesCount[this.recentNotes[a]] === undefined) notesCount[this.recentNotes[a]] = 0;
                    notesCount[this.recentNotes[a]]++;
                }
                let mostRecent = 0;
                let theOne = 0;
                for (let a = 0; a < notesCount.length; a++) {
                    if (notesCount[a] !== undefined) {
                        if (notesCount[a] > mostRecent) {
                            mostRecent = notesCount[a];
                            theOne = a;
                        }
                    }
                }

                let returnPitch = theOne - 48;
                if (returnPitch <= 36) {
                    this.gotRecInput(returnPitch);
                }
            }
        }
        if (!window.requestAnimationFrame) window.requestAnimationFrame = (window as any).webkitRequestAnimationFrame;
        this.rafID = window.requestAnimationFrame(this.updatePitch);
    };
    getAverageVolume = (array: Uint8Array) => {
        let values = 0;
        let average;

        const length = array.length;

        // get all the frequency amplitudes
        for (let i = 0; i < length; i++) {
            values += array[i];
        }

        average = values / length;
        return average;
    };
}

export default Recorder;
